import InputField from 'components/fields/InputField';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { formatDate } from 'pages/helpers/dateUtils';
import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';

const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};
const formatTime = (time) => {
  if (!time) return 'N/A';
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${suffix}`;
};

const Post = ({
  batches = [],
  onEditBatchApi,
  onDeleteBatchApi,
  onBatchUpdated,
  selectedStructure,
  selectedBranch,
}) => {
  const [filter, setFilter] = useState('all');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [isStudentsModalOpen, setIsStudentsModalOpen] = useState(false);
  const [studentsList, setStudentsList] = useState([]);

  const [updateConfirmation, setUpdateConfirmation] = useState({
    isOpen: false,
    batchData: null,
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    batchId: null,
  });

  /* ================= FILTER + COUNTS ================= */

  const filteredBatches = useMemo(() => {
    if (filter === 'single')
      return batches.filter((b) => b.branches?.length === 1);
    if (filter === 'multi')
      return batches.filter((b) => b.branches?.length > 1);
    return batches;
  }, [filter, batches]);

  const counts = useMemo(
    () => ({
      total: batches.length,
      single: batches.filter((b) => b.branches?.length === 1).length,
      multi: batches.filter((b) => b.branches?.length > 1).length,
    }),
    [batches]
  );

  /* ================= EDIT ================= */

  const handleEditBatch = (batch) => {
    setSelectedBatch({
      ...batch,
      startDate: formatDateForInput(batch.startDate),
      endDate: formatDateForInput(batch.endDate),
      daySchedules: batch.daySchedules || [],
    });
    setIsEditModalOpen(true);
  };

  const toggleDay = (day) => {
    setSelectedBatch((prev) => {
      const exists = prev.daySchedules.some((d) => d.day === day);
      return {
        ...prev,
        daySchedules: exists
          ? prev.daySchedules.filter((d) => d.day !== day)
          : [...prev.daySchedules, { day, startTime: '', endTime: '' }],
      };
    });
  };

  const updateTime = (day, field, value) => {
    setSelectedBatch((prev) => ({
      ...prev,
      daySchedules: prev.daySchedules.map((d) =>
        d.day === day ? { ...d, [field]: value } : d
      ),
    }));
  };

  const handleUpdateConfirm = (e) => {
    e.preventDefault();

    if (!selectedBatch.daySchedules.length) {
      toast.error('Please select at least one day');
      return;
    }

    setUpdateConfirmation({
      isOpen: true,
      batchData: {
        batchId: selectedBatch._id,
        fees: Number(selectedBatch.fees),
        startDate: selectedBatch.startDate,
        endDate: selectedBatch.endDate,
        daySchedules: selectedBatch.daySchedules.map(
          ({ day, startTime, endTime }) => ({
            day,
            startTime,
            endTime,
          })
        ),
      },
    });
  };

  const performBatchUpdate = async () => {
    try {
      const res = await onEditBatchApi(updateConfirmation.batchData);

      if (res?.data?.success) {
        toast.success(res.data.message || 'Batch updated');
        setIsEditModalOpen(false);

        await onBatchUpdated(); // 🔥 REFRESH LIST
      } else {
        toast.error(res?.data?.message || 'Update failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error updating batch');
    } finally {
      setUpdateConfirmation({ isOpen: false, batchData: null });
    }
  };

  /* ================= DELETE ================= */

  const performBatchDelete = async () => {
    try {
      const res = await onDeleteBatchApi(deleteConfirmation.batchId);
      if (res?.data?.success) {
        toast.success(res.data.message || 'Batch deleted');
        onBatchUpdated?.();
      } else {
        toast.error(res?.data?.message || 'Delete failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error deleting batch');
    } finally {
      setDeleteConfirmation({ isOpen: false, batchId: null });
    }
  };
  const handleViewStudents = (batch) => {
    setIsStudentsModalOpen(true);
    setStudentsList(batch.studentsData || []);
  };

  /* ================= UI ================= */

  return (
    <div>
      <div className="mb-4 flex gap-3">
        {[
          { key: 'all', label: 'All', count: counts.total },
          { key: 'single', label: 'Single Branch', count: counts.single },
          { key: 'multi', label: 'Multi Branch', count: counts.multi },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`flex gap-2 rounded px-4 py-2 text-sm font-semibold ${
              filter === f.key ? 'bg-brand-900 text-white' : 'bg-gray-100'
            }`}
          >
            {f.label}
            <span className="rounded-full bg-gray-300 px-2 text-xs">
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {filteredBatches.length === 0 ? (
        <p className="text-gray-500">No batches found</p>
      ) : (
        filteredBatches.map((batch) => (
          <div key={batch._id} className="border-b pb-4">
            <p className="font-semibold">
              Batch {batch.batchNumber} (
              {batch.branches.length === 1 ? 'Single Branch' : 'Multi Branch'})
            </p>
            {batch.approvalStatus === 'pending' && (
              <span className="mt-1 inline-block rounded bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800">
                Pending Approval
              </span>
            )}

            {/* BRANCH LIST */}
            <p className="mt-2 text-sm font-semibold">Branches:</p>
            <ul className="ml-5 list-disc text-sm text-gray-700">
              {batch.branches?.map((branch) => (
                <li key={branch._id}>{branch.name}</li>
              ))}
            </ul>

            <p className="mt-2">
              {formatDate(batch.startDate)} – {formatDate(batch.endDate)}
            </p>
            {/* DAYS & TIMINGS */}
            {Array.isArray(batch.daySchedules) &&
              batch.daySchedules.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-semibold">Days & Timings:</p>
                  <ul className="ml-5 list-disc text-sm text-gray-700">
                    {batch.daySchedules.map((schedule) => (
                      <li key={schedule._id}>
                        <b>{schedule.day}:</b> {formatTime(schedule.startTime)}{' '}
                        – {formatTime(schedule.endTime)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            <p>Fees: ₹{batch.fees}</p>
            <p>Mode: {batch.mode}</p>
            <p>
              <b>Students Enrolled:</b> {batch.studentsEnrolled || 0}
            </p>

            <div className="mt-3 flex gap-3">
              <button
                onClick={() => handleEditBatch(batch)}
                className="rounded bg-indigo-500 px-3 py-2 text-white"
              >
                Edit Batch
              </button>

              <button
                onClick={() =>
                  setDeleteConfirmation({
                    isOpen: true,
                    batchId: batch._id,
                  })
                }
                className="rounded bg-red-500 px-3 py-2 text-white"
              >
                Delete Batch
              </button>

              <button
                onClick={() => handleViewStudents(batch)}
                className="rounded bg-green-500 px-3 py-2 text-white"
              >
                View Student
              </button>
            </div>
          </div>
        ))
      )}

      {/* ================= EDIT MODAL ================= */}

      {isEditModalOpen && selectedBatch && (
        <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center">
          <div className="relative w-full max-w-lg rounded bg-white p-4">
            {/* ❌ CLOSE BUTTON */}
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="absolute right-3 top-3 text-xl font-bold"
            >
              ✕
            </button>

            <form onSubmit={handleUpdateConfirm}>
              <p className="mb-2 font-bold">
                Batch {selectedBatch.batchNumber}
              </p>

              <InputField
                label="Fees"
                value={selectedBatch.fees}
                onChange={(e) =>
                  setSelectedBatch({ ...selectedBatch, fees: e.target.value })
                }
              />

              <InputField
                type="date"
                label="Start Date"
                value={selectedBatch.startDate}
                onChange={(e) =>
                  setSelectedBatch({
                    ...selectedBatch,
                    startDate: e.target.value,
                  })
                }
              />

              <InputField
                type="date"
                label="End Date"
                value={selectedBatch.endDate}
                onChange={(e) =>
                  setSelectedBatch({
                    ...selectedBatch,
                    endDate: e.target.value,
                  })
                }
              />

              {/* DAYS */}
              <p className="mt-4 font-semibold">Days & Time</p>

              {DAYS.map((day) => {
                const active = selectedBatch.daySchedules.some(
                  (d) => d.day === day
                );
                const schedule = selectedBatch.daySchedules.find(
                  (d) => d.day === day
                );

                return (
                  <div key={day} className="mt-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleDay(day)}
                    />
                    <span className="w-24">{day}</span>

                    {active && (
                      <>
                        <InputField
                          type="time"
                          value={schedule.startTime}
                          onChange={(e) =>
                            updateTime(day, 'startTime', e.target.value)
                          }
                        />
                        <InputField
                          type="time"
                          value={schedule.endTime}
                          onChange={(e) =>
                            updateTime(day, 'endTime', e.target.value)
                          }
                        />
                      </>
                    )}
                  </div>
                );
              })}

              <button className="mt-4 w-full rounded bg-blue-500 py-2 text-white">
                Update Batch
              </button>
            </form>
          </div>
        </div>
      )}

      {/* CONFIRM MODALS */}

      <ConfirmationModal
        isOpen={updateConfirmation.isOpen}
        onClose={() =>
          setUpdateConfirmation({ isOpen: false, batchData: null })
        }
        onConfirm={performBatchUpdate}
        title="Confirm Update"
        message="Are you sure you want to update this batch?"
      />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, batchId: null })}
        onConfirm={performBatchDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this batch?"
      />

      {/* ================= STUDENTS MODAL ================= */}
      {isStudentsModalOpen && (
        <div className="bg-black/50 backdrop-blur-sm fixed inset-0 z-50 flex items-center justify-center">
          <div className="relative w-full max-w-4xl rounded bg-white p-6 max-h-[80vh] overflow-y-auto">
            <button
              onClick={() => setIsStudentsModalOpen(false)}
              className="absolute right-4 top-4 text-xl font-bold"
            >
              ✕
            </button>

            <h2 className="mb-4 text-xl font-bold">Students in Batch</h2>

            {studentsList.length === 0 ? (
              <p className="text-gray-500">No students found in this batch</p>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 border">Sr No</th>
                    <th className="p-2 border">Name</th>
                    <th className="p-2 border">Email</th>
                    <th className="p-2 border">Contact</th>
                    <th className="p-2 border">Course</th>
                  </tr>
                </thead>
                <tbody>
                  {studentsList.map((student, index) => (
                    <tr key={student._id}>
                      <td className="p-2 border">{index + 1}</td>
                      <td className="p-2 border">{student.name}</td>
                      <td className="p-2 border">{student.email}</td>
                      <td className="p-2 border">{student.contactNumber}</td>
                      <td className="p-2 border">
                        {student.enrollments?.map((e) => e.course?.courseName).filter(Boolean).join(', ') || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Post;
